/**
 * Premium "Partner Offer Reveal" — Partner-Header + enthüllte Angebot-Karten.
 *
 * Die „Link rein → Angebote raus"-Szene: oben ein kompakter Partner-Header
 * (Logo + Name + Stadt + Kategorie-Pill), darunter 2–4 Angebot-Karten mit
 * Produktbild, Titel und Preis-Badge, die nacheinander enthüllt werden
 * (Karte gleitet, Bild zoomt sanft, Preis-Badge poppt). Abschluss: pulsierende
 * Allyver-URL-Pille.
 *
 * Alles wird über den „Aus Allyver-Händler-Link laden"-Loader im Inspector
 * automatisch befüllt — Bilder, Titel und Preise sind danach frei austauschbar.
 *
 * Inputs:
 *   text     – Partner-Name
 *   subtext  – Stadt / Standort
 *   url      – CTA-/Shop-URL (Default: allyver.com)
 *   media[0] – Partner-Logo
 *   items    – Angebote im Format `Titel | Beschreibung | Preis | BildURL` (max 4)
 *   extras.kategorieType – Kategorie-Pill (z. B. „PRODUKT")
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine, makeTl,
  pickMediaSrc, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

type Offer = { title: string; description?: string; price?: string; imageUrl?: string };

function parseOffer(raw: string): Offer {
  const parts = String(raw ?? "").split("|").map((s) => s.trim());
  return {
    title: parts[0] ?? "",
    description: parts[1] || undefined,
    price: parts[2] || undefined,
    imageUrl: parts[3] || undefined,
  };
}

export const partnerOfferReveal: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = inputs.accent ? accentFor(inputs) : themeColors.primary;
  const isVertical = ctx.format === "9:16";
  const name = (inputs.text ?? "PARTNER").trim();
  const city = (inputs.subtext ?? "").trim();
  const url = (inputs.url ?? "allyver.com").trim().replace(/^https?:\/\//, "").replace(/\/$/, "");
  const kategorie = String(inputs.extras?.kategorieType ?? inputs.extras?.category ?? "ANGEBOTE").trim();
  const logoUrl = pickMediaSrc(inputs.media, 0);

  const offers = (inputs.items?.length
    ? inputs.items
    : [
        "Signature-Angebot | Frisch & lokal gemacht | 12,90 € | ",
        "Bestseller | Beliebt bei Stammkund:innen | 8,50 € | ",
        "Neu im Sortiment | Jetzt entdecken | 19,00 € | ",
      ]
  ).slice(0, 4).map(parseOffer);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const logoSize = isVertical ? 116 : 96;
  const nameSize = (isVertical ? lg : md) * 0.78;
  const citySize = sm * 0.74;
  const eyeSize = typeSize("xs", ctx.format) * 0.95;
  const offTitlePx = isVertical ? md * 0.58 : md * 0.5;
  const offDescPx = sm * 0.58;
  const pricePx = isVertical ? md * 0.6 : md * 0.52;
  const ctaSize = sm * 0.82;
  const thumb = isVertical ? 132 : 116;

  const t = makeTl(clipStartSec);
  const headId = `${clipPrefix}-head`;
  const hLogoId = `${clipPrefix}-hlogo`;
  const catId = `${clipPrefix}-cat`;
  const cardIds = offers.map((_, i) => `${clipPrefix}-card-${i}`);
  const imgIds = offers.map((_, i) => `${clipPrefix}-img-${i}`);
  const priceIds = offers.map((_, i) => `${clipPrefix}-price-${i}`);
  const ctaId = `${clipPrefix}-cta`;
  const ctaGlowId = `${clipPrefix}-cta-glow`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  const headLogo = logoUrl
    ? `<img id="${hLogoId}" src="${escapeHtml(logoUrl)}" alt="logo" style="width:${logoSize}px;height:${logoSize}px;object-fit:contain;border-radius:24px;background:${withAlpha(themeColors.fg, 0.06)};border:1.5px solid ${withAlpha(themeColors.fg, 0.18)};padding:10px;box-shadow:0 12px 30px rgba(0,0,0,0.45);" />`
    : `<div id="${hLogoId}" style="width:${logoSize}px;height:${logoSize}px;border-radius:24px;background:linear-gradient(135deg, ${accent} 0%, ${withAlpha(accent, 0.6)} 100%);display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${logoSize * 0.5}px;color:${themeColors.bg};box-shadow:0 12px 30px ${withAlpha(accent, 0.55)};">${escapeHtml(name.charAt(0))}</div>`;

  const headerHtml = `<div id="${headId}" style="display:flex;align-items:center;gap:22px;width:100%;max-width:${isVertical ? 920 : 1180}px;padding:18px 24px;background:linear-gradient(180deg, ${withAlpha(themeColors.fg, 0.07)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%);border:1.5px solid ${withAlpha(themeColors.fg, 0.16)};border-radius:28px;backdrop-filter:blur(14px);box-shadow:0 24px 60px rgba(0,0,0,0.4);">
    ${headLogo}
    <div style="display:flex;flex-direction:column;gap:5px;flex:1;min-width:0;">
      <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize}px;letter-spacing:5px;text-transform:uppercase;color:${accent};">— Allyver Partner</div>
      <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${nameSize}px;line-height:1;letter-spacing:-1.5px;color:${themeColors.fg};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(name)}</div>
      ${city ? `<div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${citySize}px;color:${withAlpha(themeColors.fg, 0.7)};display:flex;align-items:center;gap:6px;">
        <svg width="${Math.round(citySize)}" height="${Math.round(citySize)}" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
        <span>${escapeHtml(city)}</span>
      </div>` : ""}
    </div>
    <div id="${catId}" style="flex-shrink:0;display:inline-flex;align-items:center;padding:8px 18px;border-radius:9999px;background:${withAlpha(accent, 0.18)};border:1px solid ${withAlpha(accent, 0.5)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize}px;letter-spacing:2.5px;text-transform:uppercase;color:${accent};">${escapeHtml(kategorie)}</div>
  </div>`;

  function renderCard(i: number, o: Offer): string {
    const img = pickMediaSrc(o.imageUrl ?? null, 0);
    const imgHtml = img
      ? `<img id="${imgIds[i]}" src="${escapeHtml(img)}" alt="${escapeHtml(o.title)}" style="width:100%;height:100%;object-fit:cover;display:block;" />`
      : `<div id="${imgIds[i]}" style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:radial-gradient(circle at 35% 30%, ${withAlpha(accent, 0.45)} 0%, ${withAlpha(accent, 0.1)} 70%, ${withAlpha(themeColors.bg, 0.85)} 100%);color:${themeColors.fg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${thumb * 0.36}px;">${escapeHtml((o.title || "A").charAt(0).toUpperCase())}</div>`;
    return `<div id="${cardIds[i]}" style="display:flex;align-items:center;gap:${isVertical ? 22 : 24}px;padding:${isVertical ? 16 : 14}px;border-radius:24px;background:linear-gradient(180deg, ${withAlpha(themeColors.fg, 0.06)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%);border:1px solid ${withAlpha(themeColors.fg, 0.14)};border-left:4px solid ${accent};box-shadow:0 16px 38px rgba(0,0,0,0.32);overflow:hidden;">
      <div style="flex-shrink:0;width:${thumb}px;height:${thumb}px;border-radius:18px;overflow:hidden;background:${withAlpha(themeColors.fg, 0.06)};border:1px solid ${withAlpha(themeColors.fg, 0.12)};">${imgHtml}</div>
      <div style="flex:1;display:flex;flex-direction:column;gap:6px;min-width:0;">
        <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${offTitlePx}px;line-height:1.08;letter-spacing:-0.8px;color:${themeColors.fg};text-wrap:balance;">${escapeHtml(o.title)}</div>
        ${o.description ? `<div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${offDescPx}px;line-height:1.3;color:${withAlpha(themeColors.fg, 0.68)};display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;">${escapeHtml(o.description)}</div>` : ""}
      </div>
      ${o.price ? `<div id="${priceIds[i]}" style="flex-shrink:0;align-self:center;padding:10px 18px;border-radius:16px;background:linear-gradient(135deg, ${accent} 0%, ${withAlpha(themeColors.secondary, 0.92)} 100%);color:${themeColors.bg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${pricePx}px;letter-spacing:-0.5px;box-shadow:0 0 26px ${withAlpha(accent, 0.5)};white-space:nowrap;">${escapeHtml(o.price)}</div>` : `<div id="${priceIds[i]}" style="opacity:0;"></div>`}
    </div>`;
  }

  const cardsHtml = offers.map((o, i) => renderCard(i, o)).join("");

  const ctaHtml = `<div id="${ctaId}" style="position:relative;display:inline-flex;align-items:center;gap:14px;padding:${isVertical ? 18 : 16}px ${isVertical ? 36 : 30}px;border-radius:9999px;background:linear-gradient(180deg, ${accent} 0%, ${withAlpha(accent, 0.85)} 100%);box-shadow:0 16px 42px ${withAlpha(accent, 0.55)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.45)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${ctaSize}px;letter-spacing:2px;text-transform:uppercase;color:${themeColors.bg};">
    <div id="${ctaGlowId}" style="position:absolute;inset:-12px;border-radius:9999px;background:radial-gradient(50% 50% at 50% 50%, ${withAlpha(accent, 0.5)} 0%, transparent 70%);filter:blur(8px);pointer-events:none;opacity:0;"></div>
    <svg width="${Math.round(ctaSize)}" height="${Math.round(ctaSize)}" viewBox="0 0 24 24" fill="none" stroke="${themeColors.bg}" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
    <span>${escapeHtml(url)}</span>
  </div>`;

  const padTopBot = isVertical ? 90 : 60;
  const padX = isVertical ? 58 : 90;

  const html = `<div id="${clipPrefix}-pad" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 20 : 18}px;padding:${padTopBot}px ${padX}px;">
    ${headerHtml}
    <div style="display:flex;flex-direction:column;gap:${isVertical ? 16 : 14}px;width:100%;max-width:${isVertical ? 920 : 1180}px;">${cardsHtml}</div>
    ${ctaHtml}
  </div>`;

  const init = [
    gsapSetLine(`#${headId}`, { opacity: 0, y: -26 }),
    gsapSetLine(`#${catId}`, { opacity: 0, scale: 0.8 }),
    ...cardIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, x: 64 })),
    ...imgIds.map((id) => gsapSetLine(`#${id}`, { scale: 1.18 })),
    ...priceIds.map((id, i) => (offers[i].price ? gsapSetLine(`#${id}`, { scale: 0, transformOrigin: "50% 50%" }) : "")),
    gsapSetLine(`#${ctaId}`, { opacity: 0, y: 24, scale: 0.92 }),
    gsapSetLine(`#${ctaGlowId}`, { opacity: 0 }),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.65, ease: "power3.out" }, 0.05),
    t.to(`#${catId}`, { opacity: 1, scale: 1, duration: 0.45, ease: "back.out(2.0)" }, 0.4),
  ];

  const start = 0.6;
  const ctaTail = 1.4;
  const revealWindow = Math.max(1.0, Math.min(offers.length * 0.55, clipDurationSec - start - ctaTail));
  const step = revealWindow / Math.max(offers.length, 1);

  offers.forEach((o, i) => {
    const at = start + i * step;
    tweens.push(t.to(`#${cardIds[i]}`, { opacity: 1, x: 0, duration: 0.5, ease: "power3.out" }, at));
    tweens.push(t.to(`#${imgIds[i]}`, { scale: 1, duration: 0.8, ease: "power2.out" }, at));
    if (o.price) tweens.push(t.to(`#${priceIds[i]}`, { scale: 1, duration: 0.45, ease: "back.out(2.4)" }, at + 0.18));
  });

  const ctaAt = start + offers.length * step + 0.1;
  tweens.push(t.to(`#${ctaId}`, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "back.out(1.6)" }, ctaAt));
  tweens.push(t.to(`#${ctaGlowId}`, { opacity: 1, duration: 0.5, ease: "power2.out" }, ctaAt + 0.1));
  const dwell = Math.max(0.6, clipDurationSec - ctaAt - 1.0);
  tweens.push(t.to(`#${ctaGlowId}`, { opacity: 0.4, duration: dwell, repeat: -1, yoyo: true, ease: "sine.inOut" }, ctaAt + 0.6));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
